import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { formValues, schema } from "./validations";
import { CHECKLIST, QUALITY_CHECK } from "./consts";
import { getRequiredFields, planStatus, removeEmptyFields } from "@/lib/utils";
import { useEffect, useMemo, useState } from "react";
import { useAddQualityCheckMutation, useGetQualityChecklistWithItemByProject, useGetQualityCheckUpdates, useGetQualityChecks, useUpdateQualityCheckMutation } from "@/services";
import { useParams } from "react-router-dom";
import { toast } from "@/components";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useQueryClient } from "@tanstack/react-query";
import { routes } from "@/routes/utils";
import { setChecksData } from "@/redux/slices";

const useQualityCheck = () => {
  const {id, action, planAction} = useParams();
  const isView = action == routes.view || planAction == routes.view;
  const [openTabs, setOpenTabs] = useState<any>("1");
  const [isPhotoSliderOpen, setIsPhotoSliderOpen] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState<any[]>([]);
  const [activeCheckForSlider, setActiveCheckForSlider] = useState<any>(null);
  const [isNotesDialogOpen, setIsNotesDialogOpen] = useState(false);
  const [clickedType, setClickedType] = useState<"attachment" | "notes" | null>(null);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const form = useForm<formValues>({
    defaultValues: {
      [QUALITY_CHECK]: [],
      [CHECKLIST]: {},
    },
    resolver: zodResolver(schema),
  });


  const requiredFields = getRequiredFields(schema);

  const { taskData, planData } = useSelector((state: RootState) => state.currentData)

  const isPlanApproved = useMemo(() => planData?.status == planStatus.approved.value, [planData]);

  const {data: qualityChecklist, isLoading: isLoadingChecklist} = useGetQualityChecklistWithItemByProject({
    projectId: id as string
  })

  const {data: qualityChecks, isLoading: isLoadingChecks} = useGetQualityChecks({
    projectId: id as string,
    taskId: taskData?.taskId
  })

  const { data: attachmentList, isLoading: isAttachmentsLoading } = useGetQualityCheckUpdates({
    taskId: taskData?.taskId,
    projectId: id as string,
    checkId: activeCheckForSlider?.taskQualityCheckId,
    queryParams: {
      enabled: !!activeCheckForSlider?.taskQualityCheckId
    }
  })

  useEffect(() => {
    if (attachmentList?.data && activeCheckForSlider && clickedType) {
      if (clickedType === "attachment") {
        const allPhotos = attachmentList.data.reduce((acc: any[], update: any) => {
          const photosFromUpdate = update.attachments
            ?.filter((a: any) => a.presignedUrl)
            ?.map((a: any) => ({
              url: a.presignedUrl,
              description: a.fileDescription || update.description || "No description provided",
            }));
          
          return [...acc, ...(photosFromUpdate || [])];
        }, []);

        if (allPhotos.length > 0) {
          setSelectedPhotos(allPhotos);
          setIsPhotoSliderOpen(true);
        } else {
          toast({ message: "Not attachment found", type: "warn" });
        }
      } else if (clickedType === "notes") {
        setIsNotesDialogOpen(true);
      }
      setActiveCheckForSlider(null); 
      setClickedType(null);
    } else if (attachmentList && !attachmentList.data?.length && activeCheckForSlider && clickedType) {
       if (clickedType === "attachment") {
          toast({ message: "Not attachment found", type: "warn" });
       } else {
          setIsNotesDialogOpen(true);
       }
       setActiveCheckForSlider(null);
       setClickedType(null);
    }
  }, [attachmentList, activeCheckForSlider, clickedType]);
  
  useEffect(() => {
    if (qualityChecklist?.data &&  qualityChecks?.data){
      const groupedByChecklistId = qualityChecks?.data?.reduce(
        (acc: any, item: any, index: number) => {
          const key = item.taskQualityChecklistId;

          const checklistData = qualityChecklist?.data?.find((val: any) => val?.taskQualityChecklistId == key)

          if (!acc[key]) {
            acc[key] = {
              accordionId: (index + 1)?.toString(),
              taskQualityChecklistId: key,
              ...checklistData,
              items: [],
            };
          }
      
          acc[key].items.push(item);

          const items = [...acc[key].items];

          acc[key].items = items?.map((val: any) => {
            return {
              ...val,
              ...checklistData.items.find((check: any) => check.taskQualityChecklistItemId == val.taskQualityChecklistItemId)
            }
          })
      
          return acc;
        },
        {}
      );
      form.setValue(CHECKLIST, groupedByChecklistId)
    }
  }, [qualityChecklist, qualityChecks]);

  const { mutateAsync: addInfo, isPending: isAddPending } =
    useAddQualityCheckMutation({
      onError: (error?: any) => {
        toast({
          message:
            typeof error?.response?.data?.message === "string"
              ? error?.response?.data?.message
              : "Something went wrong",
          type: "error",
        });
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        // toast({ message: "Quality Checks Added Successfully" });
      },
    });

  const { mutateAsync: _updateInfo, isPending: isUpdatePending } =
    useUpdateQualityCheckMutation({
      onError: (error?: any) => {
        toast({
          message:
            typeof error?.response?.data?.message === "string"
              ? error?.response?.data?.message
              : "Something went wrong",
          type: "error",
        });
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        // toast({ message: "Quality Checks Updated Successfully" });
      },
    });

  const onAccordionChange = (e: any) => {
    setOpenTabs([e[e.length - 1]]);
  };

  const onSubmit = async (data: formValues) => {
    const _data = removeEmptyFields(data);

    const promises: Promise<any>[] = [];

    // if (taskData?.taskId) {
    //   updateInfo({projectId: id as string, taskId: taskData?.taskId as string, 
    //     data: {
    //       ..._data,
    //   }})
    // } else {
      (_data[QUALITY_CHECK] as any)?.map((val: any) => {
        qualityChecklist?.data?.find((check: any) => check.taskQualityChecklistId == val)?.items?.map((check: any) => 
          promises.push(addInfo({projectId: id as string, taskId: taskData?.taskId as string, 
            data: {
              taskQualityChecklistId: val,
              taskQualityChecklistItemId: check?.taskQualityChecklistItemId,
              status: "PENDING"
          }}))
        )
      })
    // }

    await Promise.all(promises);
    queryClient.invalidateQueries({queryKey: ["quality-checks", taskData?.taskId]});
    toast({ message: "Quality Checks Added Successfully" });
  }

    const onAttachmentClick = (checksData: any) => {
      dispatch(setChecksData(checksData));
      setActiveCheckForSlider(checksData);
      setClickedType("attachment");
    }

    const onNotesClick = (checksData: any) => {
      dispatch(setChecksData(checksData));
      setActiveCheckForSlider(checksData);
      setClickedType("notes");
    }

  return {
    form,
    requiredFields,
    openTabs,
    onAccordionChange,
    qualityChecklist,
    isLoadingChecklist,
    isAddPending,
    isUpdatePending,
    onSubmit,
    isLoadingChecks,
    qualityChecks,
    isView,
    onAttachmentClick,
    onNotesClick,
    isPhotoSliderOpen,
    setIsPhotoSliderOpen,
    isNotesDialogOpen,
    setIsNotesDialogOpen,
    selectedPhotos,
    isAttachmentsLoading,
    isPlanApproved
  };
};

export default useQualityCheck;
